import { LeadRequest, LeaseCarProspect, Store } from 'types';

import { addZeroAtStartDay, addZeroAtStartMonth } from '../../../../../utils/dateUtil';

// Suggested list taken from
// https://cheatsheetseries.owasp.org/cheatsheets/Cross_Site_Scripting_Prevention_Cheat_Sheet.html#output-encoding-for-html-contexts
const specialCharSanitiser = (x: string) =>
	x
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#x27;')
		.replace(/`/g, '&#96;');

export const mapCustomerStoreToLeadRequestProspect = (state: Store): LeaseCarProspect => {
	const { plCustomer } = state;
	const { extraInformation } = plCustomer;

	const leadRequestBirthdate = extraInformation?.birthdate || plCustomer.birthdate;

	return {
		person: {
			initials: plCustomer.initials.replace(/\s+/g, ''),
			lastName: plCustomer.lastName,
			prefix: plCustomer.lastNamePrefix || '',
			salutation: plCustomer.gender === 'FEMALE' ? 'FEMALE' : 'MALE',
			dateOfBirth: leadRequestBirthdate,
			emailAddress: extraInformation?.email || plCustomer.email,
			telephoneNumber: extraInformation?.phoneNumber || plCustomer.phoneNumber,
		},
		streetName: plCustomer.street,
		houseNumber: parseInt(plCustomer.houseNumber, 10),
		houseNumberExtension: plCustomer.houseNumberSuffix || '',
		dutchPostalCode: plCustomer.postalCode.replace(/\s+/g, ''),
		placeName: plCustomer.city,
		mobileNumber: extraInformation?.phoneNumber || plCustomer.phoneNumber,
	};
};

export const mapStoreToLeadRequest = (state: Store): LeadRequest => {
	const date = new Date();
	const chosenLeaseCarColor = state?.chosenLeaseCar?.color;
	const chosenLeaseCarUpgrade = state?.chosenLeaseCar?.upgrade;
	const leadRequest: LeadRequest = {
		promotionId: state.leaseCarData?.privateLeaseData.promotionId || 0,
		indAgreeCreditCheck: state.agreedToTerms ? 'YES' : 'NO',
		indAgreeBkr: state.agreedToTerms ? 'YES' : 'NO',
		offerDate: `${date.getFullYear()}-${addZeroAtStartMonth(date.getMonth())}-${addZeroAtStartDay(
			date.getDate()
		)}`,
		// Setting the product.id to null will trigger an error from the BE
		product: {
			id: state.leaseCarData?.privateLeaseData.productData[0].id || null,
			description: 'ANWB Standaard',
			price: 0,
		},
		productModule: [],
		privacyStatement: state.agreedToTerms,
		prospect: mapCustomerStoreToLeadRequestProspect(state),
		totalPrice: state.chosenLeaseCar.totalPrice,
		comment: specialCharSanitiser(state.remarks),
		car: {
			id: state.leaseCarData?.privateLeaseData.carId || 0,
			description: `${state.leaseCarData?.vehicleData.make} ${state.leaseCarData?.vehicleData.model}`,
			color1: {
				id: chosenLeaseCarColor?.id ? chosenLeaseCarColor.id : 0,
				description: chosenLeaseCarColor?.name ? chosenLeaseCarColor.name : '',
				price: chosenLeaseCarColor?.price ? chosenLeaseCarColor.price : 0,
			},
			carOption: [
				...(state.leaseCarData &&
				!state.isOccasion &&
				!state.isFlex &&
				state.leaseCarData.id !== state?.chosenLeaseCar?.upgrade?.id.toString()
					? [
							{
								id: chosenLeaseCarUpgrade?.id ? chosenLeaseCarUpgrade.id : 0,
								description: chosenLeaseCarUpgrade?.description
									? chosenLeaseCarUpgrade.description
									: '',
								price: chosenLeaseCarUpgrade?.monthPrice ? chosenLeaseCarUpgrade.monthPrice : 0,
							},
						]
					: []),

				...state.chosenLeaseCar.options.map((option) => ({
					id: option.id,
					description: option.description,
					price: option.monthPrice,
				})),
			],

			pricePoint: {
				pricePointId: state.chosenLeaseCar.pricePointId,
				contractTerm: parseInt(state.chosenLeaseCar.duration, 10),
				kilometrage: parseInt(state.chosenLeaseCar.kilometrage, 10),
				pricePointPrice: state.chosenLeaseCar.pricePointPrice,
			},
			service: state.chosenLeaseCar.services.map((service) => ({
				id: service.id,
			})),
		},
	};

	const { paymentInAdvanceChoice, paymentInAdvanceAmount } = state.chosenLeaseCar;

	/**
	 * The backend only needs the prepayment amount when the prepayment will be made.
	 * The amount has been validated in the form.
	 */
	if (paymentInAdvanceChoice) {
		leadRequest.prepayment = paymentInAdvanceAmount;
	}

	return leadRequest;
};
